export const SUPPORTED_LOCALES = [
  { value: "en", label: "English" },
  { value: "zh-Hans", label: "简体中文" },
  { value: "zh-Hant", label: "繁體中文" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "th", label: "ไทย" },
  { value: "ms", label: "Bahasa Melayu" },
  { value: "hi", label: "हिन्दी" },
  { value: "es", label: "Español" },
  { value: "fil", label: "Filipino" },
  { value: "ar", label: "العربية" },
  { value: "fr", label: "Français" },
  { value: "ru", label: "Русский" },
  { value: "tr", label: "Türkçe" },
  { value: "pt-BR", label: "Português (Brasil)" },
] as const;
export type AppLocale = typeof SUPPORTED_LOCALES[number]["value"];
export const LOCALE_KEY = "gameglee.locale";

/** Matches the Android CatalogLanguage contract, including historical aliases. */
export function normalizeLocale(value: string | null | undefined): AppLocale {
  const parts = (value ?? "").trim().replaceAll("_", "-").toLowerCase().split("-");
  const base = parts[0];
  if (base === "zh") return parts.includes("hant") || (!parts.includes("hans") && parts.some((part) => ["tw", "hk", "mo"].includes(part))) ? "zh-Hant" : "zh-Hans";
  if (base === "in") return "id";
  if (base === "tl") return "fil";
  if (base === "pt") return "pt-BR";
  return SUPPORTED_LOCALES.find((entry) => entry.value === base)?.value ?? "en";
}

export function readLocalePreference(): AppLocale {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved) return normalizeLocale(saved);
  } catch { /* Private browsing can deny storage while language switching still works. */ }
  const candidates = typeof navigator === "undefined" ? ["en"] : [navigator.language, ...(navigator.languages ?? [])];
  // Use the first supported browser language, rather than discarding the preference list.
  for (const candidate of candidates) {
    const normalized = normalizeLocale(candidate);
    if (normalized !== "en" || /^en(?:[-_]|$)/i.test(candidate)) return normalized;
  }
  return "en";
}

let currentLocale: AppLocale | undefined;
export function getCurrentLocale(): AppLocale { return currentLocale ?? readLocalePreference(); }
export function setCurrentLocale(value: string): AppLocale {
  currentLocale = normalizeLocale(value);
  try { localStorage.setItem(LOCALE_KEY, currentLocale); } catch { /* Keep the in-memory choice. */ }
  return currentLocale;
}
